import React, { useState } from 'react';
import {
  TextField,
  Table,
  TableBody,
  TableRow,
  TableCell,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import DialogView from './DialogView';
import { useTranslation } from './LocalizationProvider';
import { useAttributePreference } from '../util/preferences';
import { useGlobalStyles } from '../util/classes';
import { altitudeUnitString } from '../util/converter';

const GeofenceDialog = ({ open, handleClose, initialItem }) => {
  const t = useTranslation();
  const classes = useGlobalStyles();
  const [item, setItem] = useState(initialItem || { name: '', radius: '50', address: '' });
  const [locationOption, setLocationOption] = useState('currentLocation');
  const [errors, setErrors] = useState({});
  const radiusUnit = useAttributePreference('altitudeUnit');

  // Update item when initialItem changes (for editing)
  React.useEffect(() => {
    if (initialItem) {
      setItem(initialItem);
      // Set location option based on whether address exists
      if (initialItem.address) {
        setLocationOption('address');
      } else {
        setLocationOption('currentLocation');
      }
    } else {
      setItem({ name: '', radius: '50', address: '' });
      setLocationOption('currentLocation');
    }
    setErrors({});
  }, [initialItem, open]);

  const validate = () => {
    const newErrors = {};
    if (!item.name || item.name.trim() === '') {
      newErrors.name = true;
    }
    if (!item.radius || item.radius <= 0) {
      newErrors.radius = true;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleLocationChange = (event) => {
    setLocationOption(event.target.value);
    if (event.target.value === 'currentLocation') {
      setItem({ ...item, name: '', radius: 50, address: '' });
    }
  };

  return (
    <DialogView
      item={item}
      open={open}
      handleClose={handleClose}
      dialogTitle={t('sharedGeofence')}
      validate={validate}
      acceptTitle="Ok"
      content={item && (
        <Table size="small">
          <TableBody>
            <TableRow>
              <TableCell className={classes.cell}>
                <FormControl component="fieldset">
                  <RadioGroup
                    row
                    aria-label="location"
                    name="location"
                    value={locationOption}
                    onChange={handleLocationChange}
                  >
                    <FormControlLabel
                      value="currentLocation"
                      control={<Radio />}
                      label={t('mapCurrentLocation')}
                    />
                    <FormControlLabel
                      value="address"
                      control={<Radio />}
                      label={t('sharedAddress')}
                    />
                  </RadioGroup>
                </FormControl>
              </TableCell>
            </TableRow>
            {locationOption === 'address' && (
              <TableRow>
                <TableCell className={classes.cell}>
                  <TextField
                    fullWidth
                    value={item.address || ''}
                    label={t('sharedAddress')}
                    onChange={(event) => setItem({ ...item, address: event.target.value })}
                  />
                </TableCell>
              </TableRow>
            )}
            <TableRow>
              <TableCell className={classes.cell}>
                <TextField
                  required
                  error={errors.name}
                  value={item.name || ''}
                  label={t('sharedName')}
                  onChange={(event) => {
                    setItem({ ...item, name: event.target.value });
                    if (errors.name) setErrors({ ...errors, name: false });
                  }}
                  helperText={errors.name ? t('sharedRequired') : ''}
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.cell}>
                <TextField
                  required
                  type="number"
                  error={errors.radius}
                  value={item.radius || ''}
                  onChange={(event) => {
                    setItem({ ...item, radius: event.target.value });
                    if (errors.radius) setErrors({ ...errors, radius: false });
                  }}
                  label={`${t('commandRadius')} (${altitudeUnitString(radiusUnit, t)})`}
                  helperText={errors.radius ? t('sharedRequired') : ''}
                />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      )}
    />
  );
};

export default GeofenceDialog;
